import type { ReactNode } from "react"

import * as Icones from "./Icones"
import { Logotipo } from "./Logotipo"
import { cn } from "../lib/cn"

const RECURSOS = [
  {
    chave: "vigencia",
    Icone: Icones.Vigente,
    titulo: "Vigência e prazos",
    descricao: "Calcula vigência, aditivos e vencimentos sem depender de planilha.",
  },
  {
    chave: "fiscalizacao",
    Icone: Icones.Conferida,
    titulo: "Fiscalização",
    descricao: "Designação de fiscal, conferência mensal e ocorrências num só lugar.",
  },
  {
    chave: "processo",
    Icone: Icones.Abrir,
    titulo: "Processo e evidências",
    descricao: "As peças do processo administrativo, ligadas ao contrato.",
  },
  {
    chave: "alteracoes",
    Icone: Icones.Assinar,
    titulo: "Alterações contratuais",
    descricao: "Aditivos, apostilamentos e reajustes, com o limite de acréscimo calculado.",
  },
] as const

export const SOBRETITULO_DA_ENTRADA = "mt-0 mb-0 text-(length:--texto-xs) tracking-[0.12em] text-(--marca-forte) uppercase"

export function MolduraDeEntrada({ largo = false, children }: { largo?: boolean; children: ReactNode }) {
  return (
    <div className="relative grid min-h-screen grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] bg-(--fundo) max-[901px]:grid-cols-[minmax(0,1fr)] min-[901px]:max-[1180px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div
        data-slot="painel-da-marca"
        className="relative flex flex-col overflow-hidden bg-(image:--fusao-entrada) px-(--esp-7) py-(--esp-6) max-[901px]:hidden"
      >
        <div className="relative z-1 flex max-w-[38rem] flex-auto flex-col justify-center">
          <p className="m-0 flex items-baseline gap-(--esp-2) text-[clamp(36px,4.2vw,64px)] leading-none whitespace-nowrap font-bold tracking-[-0.02em] text-white">
            ATHUS Contratos
            <span className="inline-block size-5 rounded-[5px] bg-(--entrada-ponto)" aria-hidden="true" />
          </p>
          <p className="mt-(--esp-4) mb-0 max-w-[40ch] text-(length:--texto-lg) leading-(--entrelinha-normal) text-(--azul-200)">
            Gestão de contratos administrativos, prazos e fiscalização num só sistema.
          </p>

          <div className="mt-(--esp-5) grid grid-cols-2 gap-(--esp-3)">
            {RECURSOS.map((recurso) => (
              <div
                key={recurso.chave}
                data-slot="recurso"
                className="flex flex-col gap-(--esp-2) rounded-(--raio-3) border border-(--entrada-cartao-borda) bg-(--entrada-cartao-fundo) p-(--esp-4) transition-[border-color,translate] duration-(--mov-rapido) ease-(--mov-curva) hover:-translate-y-0.5 hover:border-(--azul-destaque) motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <span
                  className="flex size-[34px] items-center justify-center rounded-(--raio-2) bg-(--marca-escura) text-white"
                  aria-hidden="true"
                >
                  <recurso.Icone tamanho={18} />
                </span>
                <strong className="text-(length:--texto-sm) font-(weight:--peso-forte) text-white">{recurso.titulo}</strong>
                <p className="m-0 text-(length:--texto-xs) leading-(--entrelinha-normal) text-(--entrada-texto-do-cartao)">
                  {recurso.descricao}
                </p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-1 mt-(--esp-6) mb-0 border-t border-(--entrada-cartao-borda) pt-(--esp-5) text-(length:--texto-xs) text-(--entrada-texto-do-cartao)">
          © {new Date().getFullYear()} ATHUS Contratos — Gestão de contratos administrativos (Lei 14.133/2021).
        </p>
      </div>

      <div
        data-slot="marca-dagua"
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-[57.5%] z-0 aspect-[100/110] h-[min(640px,85%)] -translate-x-1/2 max-[901px]:hidden min-[901px]:max-[1180px]:left-1/2"
      >
        <Logotipo monocromatico className="absolute inset-0 size-full text-(--entrada-marca-dagua) [clip-path:inset(0_50%_0_0)]" />
        <Logotipo monocromatico className="absolute inset-0 size-full text-(--borda) [clip-path:inset(0_0_0_50%)]" />
      </div>

      <div className="relative flex items-center justify-center p-(--esp-6) max-[420px]:p-(--esp-3) min-[901px]:max-[1180px]:p-(--esp-5)">
        <div
          data-slot="cartao-da-entrada"
          className={cn(
            "relative z-1 flex w-full flex-col rounded-(--raio-4) bg-(--painel) p-(--esp-7) shadow-(--sombra-elevada) max-[420px]:px-(--esp-4) max-[420px]:py-(--esp-5) min-[901px]:max-[1180px]:px-(--esp-5) min-[901px]:max-[1180px]:py-(--esp-6)",
            largo ? "max-w-[520px]" : "max-w-[440px]",
          )}
        >
          <p
            data-slot="marca-no-celular"
            aria-hidden="true"
            className="mt-0 mb-(--esp-5) flex items-center gap-(--esp-2) text-(length:--texto-2xl) leading-none font-bold tracking-[-0.01em] text-(--tinta) min-[901px]:hidden"
          >
            <Logotipo className="h-[34px] w-[31px] flex-none" />
            ATHUS Contratos
            <span className="inline-block size-[7px] self-end rounded-[2px] bg-(--entrada-ponto)" />
          </p>
          {children}
        </div>
      </div>
    </div>
  )
}
